import {
  Badge,
  Button,
  ButtonGroup,
  FormGroup,
  Input,
  Label,
  Select,
} from '@/components'
import { useAxios } from '@/context/axiosContext'
import { useEffect, useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { useLocation, useNavigate } from 'react-router-dom'
import { formatDate } from '@/utils/date'
import styled, { keyframes } from 'styled-components'
import { getBadgeLabel } from '../board/boardItem'
import commonUtils from '@/utils/commonUtils'
import { tableStyles } from '@/styles/dataTable/style'
import { useRedis } from '@/hooks/useRedis'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'

const blink = keyframes`
  0% { color: red; }
  25% { color: orange; }
  50% { color: yellow; }
  75% { color: white; }
  100% { color: red; }
`

const NewBadge = styled.span`
  font-size: 10px;
  font-weight: bold;
  animation: ${blink} 1s infinite;
`
const ReplyBadge = styled.span`
  font-size: 10px;
  font-weight: bold;
  animation: ${blink} 1s infinite;
`

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

interface DataRow {
  rnum: number
  nttSn: number
  bbsTy: string
  lclasCd: string
  mlsfcCd: string
  bbsSj: string
  nttSj: string
  answerCnt: string
  updId: string
  updDate: string
  useYn: string
  replyYn: string
}

const InqrMgmt: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const [data, setData] = useState<DataRow[]>([])
  const { getComCodes, getComCodeValue } = useRedis()

  // const [inqrSj, setInqrSj] = useState({
  //   inqrSj: '',
  //   srchOpt: '',
  //   srchStr: '',
  // })
  const [srchData, setSrchData] = useState(
    location.state?.srchData || {
      srchOpt: '00',
      srchType: '',
      srchStr: '',
      openYn: '',
      useYn: '',
    },
  )
  const [currentPage, setCurrentPage] = useState(location.state?.page || 1)
  const [perPage, setPerPage] = useState(location.state?.perPage || 10)

  const isNew = (updatedAt: string) => {
    const currentTime = new Date()
    const year = parseInt(updatedAt.substring(0, 4), 10)
    const month = parseInt(updatedAt.substring(4, 6), 10) - 1 // JS의 month는 0부터 시작
    const day = parseInt(updatedAt.substring(6, 8), 10)
    const hour = parseInt(updatedAt.substring(8, 10), 10)
    const minute = parseInt(updatedAt.substring(10, 12), 10)
    const second = parseInt(updatedAt.substring(12, 14), 10)

    const updatedDate = new Date(year, month, day, hour, minute, second)

    // 24시간(하루) 차이 계산
    const diffInMs = currentTime.getTime() - updatedDate.getTime()
    const oneDayInMs = 24 * 60 * 60 * 1000
    return diffInMs <= oneDayInMs
  }
  const titleRefactoring = (title: string) => {
    if (title.length > 12) return title.substring(0, 9) + '...'
    else return title
  }
  const columns: TableColumn<DataRow>[] = [
    {
      name: 'No.',
      selector: (row) => row.rnum,
      sortable: true,
      center: true,
      wrap: true,
      width: '10%',
      cell: (row) => (
        <div style={{ width: '100%', textAlign: 'right' }}>{row.rnum}</div>
      ),
    },
    {
      name: '답변여부',
      cell: (row) => getComCodeValue('REPLY_YN', row.replyYn)?.codeNm,
      sortable: true,
      center: true,
      wrap: true,
      width: '10%',
      conditionalCellStyles: [
        {
          when: (row: DataRow) => row.replyYn === 'Y',
          style: {
            color: 'red',
          },
        },
      ],
    },
    {
      name: '문의사항 제목',
      cell: (row) => (
        <>
          {row.nttSj}
          {isNew(row.updDate) && <NewBadge>&nbsp;new</NewBadge>}
        </>
      ),
      sortable: true,
      width: '40%',
    },
    {
      name: '사용여부', // detail에 사용여부로 되어있어서 통일하려고 노출여부에서 수정함
      cell: (row) => getComCodeValue('USE_YN', row.useYn)?.codeNm,
      selector: (row) => row.useYn,
      sortable: true,
      center: true,
      wrap: true,
      conditionalCellStyles: [
        {
          when: (row: DataRow) => row.useYn === 'Y',
          style: {
            color: 'red',
          },
        },
      ],
      width: '10%',
    },
    {
      name: '수정자',
      selector: (row) => row.updId,
      sortable: true,
      center: true,
      wrap: true,
      width: '10%',
    },
    {
      name: '수정일',
      selector: (row) => row.updDate,
      format: (row) => commonUtils.formatDate(row.updDate, false),
      sortable: true,
      wrap: true,
      width: '10%',
    },
    {
      name: '',
      cell: (row) => (
        <>
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() => handleButtonUpdateClick(row.nttSn)}
          >
            수정
          </Button>
        </>
      ),
      sortable: false,
      width: '10%',
    },
  ]

  const handleSearch = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement // 타입 캐스팅
    setSrchData((prev: any) => ({ ...prev, [name]: value }))
  }

  const handleButtonUpdateClick = async (nttSn: number) => {
    navigate(`/inqrMgmt/detail`, {
      state: {
        nttSn: nttSn,
        srchData: srchData,
        page: currentPage,
        perPage: perPage,
      },
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      getInqrList()
    }
  }

  const getInqrList = async () => {
    try {
      // const params: any = {}

      // // 검색 조건이 있을 때만 params에 추가
      // if (srchData.srchOpt.trim()) {
      //   params.srchOpt = srchData.srchOpt
      // }
      // if (srchData.srchStr.trim()) {
      //   params.srchStr = srchData.srchStr
      // }
      // if (srchData.srchType.trim()) {
      //   params.srchType = srchData.srchType
      // }

      // 값이 있는 경우만 필터링
      const params = Object.fromEntries(
        Object.entries(srchData).filter(
          ([_, value]) => typeof value === 'string' && value.trim(),
        ),
      )
      const response = await axios.get('/inqrMgmt/inqrList', {
        params,
      })

      const common = response.data.common
      const data = response.data.data
      let listData = undefined
      if (common.resultCode == 'INQRS0200') {
        listData = data.inqrList
      }

      if (Array.isArray(listData)) {
        setData(listData)
      }
    } catch (error) {
      console.error('Error fetching term list:', error)
    }
  }

  useEffect(() => {
    getInqrList()
  }, [])

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '10px',
          alignItems: 'center',
          marginTop: '50px',
        }}
      >
        <p style={{ marginLeft: '20px', fontSize: '20px' }}>문의게시판 관리</p>
        <div
          style={{
            marginLeft: 'auto',
            display: 'felx',
            justifyContent: 'center',
          }}
        >
          <div style={{ display: 'flex', gap: '5px' }}>
            <FormGroup>
              <Label htmlFor={'replyYn'}>답변여부</Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="replyYn"
                name="replyYn"
                width="100px"
                onChange={handleSearch}
                value={srchData.replyYn}
              >
                <option value="">전체</option>
                {getComCodes('REPLY_YN').map((item) => (
                  <option key={'replyYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'useYn'}>사용여부</Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="useYn"
                name="useYn"
                width="100px"
                onChange={handleSearch}
                value={srchData.useYn}
              >
                <option value="">전체</option>
                {getComCodes('USE_YN').map((item) => (
                  <option key={'useYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'srchOpt'}>검색옵션</Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="srchOpt"
                name="srchOpt"
                width="100px"
                onChange={handleSearch}
                value={srchData.srchOpt}
              >
                <option value="00">전체</option>
                <option value="01">제목</option>
                <option value="02">내용</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'srchStr'}>
                <></>
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <FlexWrapper>
                <Input
                  placeholder="검색어"
                  width="300px"
                  id="srchStr"
                  name="srchStr"
                  value={srchData.srchStr}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                />
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={''}>
                <></>
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <FlexWrapper gap="10px">
                <Button
                  onClick={getInqrList}
                  color="neverbe"
                  variant="outlined"
                >
                  검색
                </Button>
              </FlexWrapper>
            </FormGroup>
          </div>
          <div style={{ color: 'red', marginTop: '5px', textAlign: 'right' }}>
            * 옵션 선택 후 검색어 없이 조회 가능
          </div>
        </div>
      </div>
      <DataTable
        // TODO 페이징처리는 서버 vs 클라이언트 (현행 클라이언트)
        columns={columns}
        data={data}
        customStyles={tableStyles}
        paginationDefaultPage={currentPage}
        onChangePage={(page) => setCurrentPage(page)}
        pagination={true}
        paginationPerPage={perPage}
        onChangeRowsPerPage={(page) => setPerPage(page)}
        paginationRowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
        noDataComponent={<>조회결과가 없습니다.</>}
      />
    </>
  )
}
export default InqrMgmt
